import clsx from "clsx";
import type { ReactNode } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

import { SEVERITY, SEVERITY_LABEL, STATUS } from "@/lib/palette";
import { compactNumber } from "@/lib/chart";

export function PageHeader({
  eyebrow,
  title,
  actions,
}: {
  eyebrow?: string;
  title: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-4 px-6 pb-4 pt-6">
      <div>
        {eyebrow && <div className="label mb-1">{eyebrow}</div>}
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({
  title,
  subtitle,
  actions,
  children,
  className,
}: {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx("card", className)}>
      {(title || actions) && (
        <div className="card-header">
          <div className="min-w-0">
            {title && <h2 className="truncate text-sm font-semibold">{title}</h2>}
            {subtitle && <p className="truncate text-xs text-ink-400">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

/**
 * A single headline figure.
 *
 * Used where a chart would be overkill - "69,778 events" is a number, and
 * drawing it as a one-bar chart would add nothing. The label sits above the
 * value so the eye lands on the figure.
 */
export function StatTile({
  label,
  value,
  unit,
  hint,
  icon,
  tone = "default",
}: {
  label: string;
  value: string | number;
  unit?: string;
  hint?: string;
  icon?: ReactNode;
  tone?: "default" | "good" | "warning" | "critical";
}) {
  const toneColor = {
    default: undefined,
    good: STATUS.good,
    warning: STATUS.warning,
    critical: STATUS.critical,
  }[tone];

  return (
    <div className="flex flex-col gap-1 rounded-xl border border-ink-700/70 bg-ink-850 px-4 py-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ink-400">
        {icon}
        <span>{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span
          className="tabular text-2xl font-semibold leading-none"
          style={toneColor ? { color: toneColor } : undefined}
        >
          {typeof value === "number" ? compactNumber(value) : value}
        </span>
        {unit && <span className="text-sm text-ink-400">{unit}</span>}
      </div>
      {hint && <div className="text-[11px] text-ink-500">{hint}</div>}
    </div>
  );
}

/**
 * Severity indicator.
 *
 * Colour plus a label, never colour alone — a status hue is meaningless to a
 * reader who cannot distinguish it, and these are the marks that matter most.
 */
export function SeverityBadge({ severity }: { severity: number }) {
  const color = SEVERITY[severity] ?? SEVERITY[5];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium"
      style={{ borderColor: `${color}55`, backgroundColor: `${color}1a`, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {SEVERITY_LABEL[severity] ?? `S${severity}`}
    </span>
  );
}

export function StatusPill({ status }: { status: string }) {
  const map: Record<string, { color: string; label: string }> = {
    complete: { color: STATUS.good, label: "Complete" },
    processing: { color: "#3987e5", label: "Processing" },
    skipped: { color: "#898781", label: "Skipped" },
    failed: { color: STATUS.critical, label: "Failed" },
  };
  const { color, label } = map[status] ?? { color: "#898781", label: status };
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]"
      style={{ backgroundColor: `${color}1a`, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-ink-400">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label ?? "Loading…"}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : "Something went wrong";
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <AlertTriangle className="h-6 w-6" style={{ color: STATUS.serious }} />
      <div>
        <p className="text-sm font-medium text-ink-200">Could not load this view</p>
        <p className="mt-0.5 text-xs text-ink-400">{message}</p>
      </div>
      {retry && (
        <button className="btn-ghost" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-1.5 rounded-xl border border-dashed border-ink-700 py-12 text-center">
      <p className="text-sm text-ink-300">{title}</p>
      {hint && <p className="max-w-md text-xs text-ink-500">{hint}</p>}
    </div>
  );
}

/**
 * Table view of a chart's data.
 *
 * Present on every chart page, because a chart alone is not an accessible
 * presentation of data — a screen-reader user, or anyone who needs an exact
 * value rather than a position, needs the numbers.
 */
export function DataTable({
  columns,
  rows,
  maxHeight = 360,
}: {
  columns: string[];
  rows: (string | number | null)[][];
  maxHeight?: number;
}) {
  return (
    <div className="overflow-auto rounded-lg border border-ink-700" style={{ maxHeight }}>
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-ink-800 text-ink-300">
          <tr>
            {columns.map((c) => (
              <th key={c} className="whitespace-nowrap px-3 py-2 font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-800">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-ink-800/50">
              {row.map((cell, j) => (
                <td
                  key={j}
                  className={clsx("whitespace-nowrap px-3 py-1.5", j > 0 && "tabular text-ink-200")}
                >
                  {cell ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
